/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { Journal, Prayer } from "@packages/sdk";
import { uuid } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  CloseIcon,
  ContentEditableDiv,
  createModal,
  cssMediaQueryMinWidthTablet,
  FullscreenModal,
  IconButton,
  numericPixels,
  ScrollArea,
  spacing,
  Text,
  useForm,
  useModal,
  zIndices,
} from "@packages/ui";
import { DialogClose, DialogTitle } from "@radix-ui/react-dialog";
import type { KeyboardEvent, MouseEventHandler } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useController } from "react-hook-form";

import { ContentItem } from "../ContentItem";

const entryStyles: Interpolation = {
  marginTop: spacing.l,
  whiteSpace: "pre-line",
  width: spacing.full,
  wordBreak: "break-word",
};

const promptStyles: Interpolation = {
  marginTop: spacing.m,
};

const entryWithPrayerStyles: Interpolation = {
  marginTop: spacing.m,
};

const formStyles: Interpolation = {
  alignItems: "flex-end",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  margin: "auto",
  maxWidth: spacing.maxWidth,
  rowGap: spacing.s,
  width: "calc(100vw - 48px)",
  paddingBottom: spacing.mediaPlayerMobile,
  [cssMediaQueryMinWidthTablet]: {
    paddingBottom: spacing.mediaPlayerDesktop,
  },
};

const titleStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsHigher,
  marginTop: spacing.l,
  width: spacing.full,
});

const contentItemStyles: Interpolation = {
  marginTop: spacing.l,
};

const placeholderStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedLow,
});

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "grid",
  gap: spacing.xxs,
  gridTemplateAreas: "'. . .'",
  gridTemplateColumns: "min-content 1fr min-content",
  gridTemplateRows: "1fr",
  justifyContent: "start",
  width: spacing.full,
};

const saveStyles: Interpolation = {
  justifySelf: "end",
};

const modalStyles: Interpolation = {
  paddingTop: numericPixels[136],
  zIndex: zIndices.modalUnderMenuBar,
};

const scrollStyles: Interpolation = {
  height: spacing.full,
  width: spacing.full,
};

export type JournalEditorProps = {
  journal?: Journal;
  prayer?: Prayer;
  onSubmit: (data: EditForm) => void;
};

type EditForm = {
  title: string;
  entry: string;
  sync_uuid: string;
  prayer_id?: number;
};

export const JournalEditor = createModal(
  ({ journal, prayer, onSubmit }: JournalEditorProps) => {
    const modal = useModal();
    const t = useTranslations();
    const submitButton = useRef<HTMLButtonElement>(null);
    const [randomUuid, setRandomUuid] = useState<string>(uuid());

    const prayerData = prayer ?? journal?.prayer ?? null;

    const DEFAULTS = useMemo(
      () => ({
        title: journal?.title ?? "",
        entry: journal?.entry ?? "",
        sync_uuid: journal?.sync_uuid ?? randomUuid,
        prayer_id: journal?.prayer_id ?? prayer?.id ?? undefined,
      }),
      [journal, prayer, randomUuid],
    );

    const {
      control,
      handleSubmit,
      reset,
      setFocus,
      formState: { isDirty, isSubmitting, isLoading, isValid },
    } = useForm<EditForm>({
      defaultValues: DEFAULTS,
    });

    const fullReset = () => {
      reset(DEFAULTS, {
        keepDirtyValues: false,
        keepErrors: false,
        keepDirty: false,
        keepValues: false,
        keepDefaultValues: false,
        keepIsSubmitted: false,
        keepIsSubmitSuccessful: false,
        keepTouched: false,
        keepIsValidating: false,
        keepIsValid: false,
        keepSubmitCount: false,
      });
      setRandomUuid(uuid());
    };

    useEffect(() => {
      if (modal?.visible) {
        fullReset();
      }
    }, [modal?.visible]);

    const handleSpecialSubmit = (e: KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === "Enter" || e.key === "Return") && e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        submitButton.current?.click();
      }
    };

    const handleOpenChange = (open: boolean) => {
      if (open) return modal.show();
      modal.resolve();
      return modal.hide();
    };

    const handleRegionalClick: MouseEventHandler<HTMLDivElement> = (e) => {
      const htmlTarget = e.target as HTMLElement;
      if (
        ["true", "plaintext-only"].includes(htmlTarget.contentEditable) ||
        htmlTarget.nodeName.toLowerCase() === "button"
      ) {
        // do nothing
        return;
      }
      setFocus("entry");
    };

    return (
      <FullscreenModal
        open={modal.visible}
        onOpenChange={handleOpenChange}
        showCloseButton={false}
        contentCSS={[modalStyles]}
        modal={false}
        contentProps={{
          onInteractOutside: (e) => e.preventDefault(),
          onClick: handleRegionalClick,
        }}
      >
        <ScrollArea css={scrollStyles}>
          <form css={formStyles} onSubmit={handleSubmit(onSubmit)}>
            <div css={headerStyles}>
              <DialogClose asChild>
                <IconButton
                  icon={<CloseIcon />}
                  variant={"transparentPrimary"}
                  size={"s"}
                />
              </DialogClose>
              <DialogTitle asChild>
                <Text type={"headline"} size={"s"}>
                  {t("post_prayer_reflections_title")}
                </Text>
              </DialogTitle>
              <Button
                ref={submitButton}
                type={"submit"}
                variant={"neutral"}
                size={"l"}
                disabled={!isDirty || isSubmitting || isLoading || !isValid}
                css={saveStyles}
              >
                {t("button_save")}
              </Button>
            </div>
            {prayerData ? (
              <ContentItem
                disabled
                size={"xs"}
                title={prayerData.title}
                imageSrc={prayerData.images}
                css={contentItemStyles}
              />
            ) : (
              <ContentEditableDiv.Field
                type={"title"}
                size={"l"}
                control={control}
                name={"title"}
                placeholder={t("journal_title_hint")}
                placeholderCSS={[placeholderStyles]}
                css={titleStyles}
                maxLength={255}
              />
            )}
            {prayerData && (
              <Text
                as={"h2"}
                type={"title"}
                size={"l"}
                css={[titleStyles, promptStyles]}
              >
                {prayerData.prompt ?? t("playlist_content_default_prompt")}
              </Text>
            )}
            <ContentEditableDiv.Field
              type={"body"}
              size={"xl"}
              control={control}
              name={"entry"}
              placeholder={
                prayerData
                  ? t("reflection_question_hint")
                  : t("journal_details_message_hint_required")
              }
              placeholderCSS={[placeholderStyles]}
              onKeyDown={handleSpecialSubmit}
              css={[entryStyles, prayerData && entryWithPrayerStyles]}
              required
            />
            <HiddenInput name={"prayer_id"} control={control} />
            <HiddenInput name={"sync_uuid"} control={control} />
          </form>
        </ScrollArea>
      </FullscreenModal>
    );
  },
);

const HiddenInput = ({ control, name }) => {
  const {
    field: { ref, value, ...field },
  } = useController({ name, control });
  return <input {...field} type={"hidden"} ref={ref} value={value} />;
};
